import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, ShieldCheck, Loader2, XCircle } from "lucide-react";

export default function Verify() {
  const [sp] = useSearchParams();
  const [rn, setRn] = useState(sp.get("rn") || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const doVerify = async (num) => {
    if (!num) return;
    setBusy(true);
    setResult(null);
    setNotFound(false);
    try {
      const { data } = await api.get(`/verify/${num.trim()}`);
      setResult(data);
    } catch {
      setNotFound(true);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (sp.get("rn")) doVerify(sp.get("rn"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pt-28 pb-20 px-6 lg:px-10 relative z-10">
      <div className="max-w-3xl mx-auto text-center">
        <div className="overline text-gold">Public Registry</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">Verify a Registration</h1>
        <p className="text-white/60 mt-3 max-w-xl mx-auto">
          Enter a Teuralink Plot Registration Number to view its public registry details. No deed can be downloaded from this page.
        </p>

        <form
          className="mt-10 glass rounded-2xl p-3 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            doVerify(rn);
          }}
        >
          <Search className="w-4 h-4 ml-3 text-white/50" />
          <input
            value={rn}
            onChange={(e) => setRn(e.target.value)}
            placeholder="TLK-M-2026-123456"
            data-testid="verify-input"
            className="flex-1 bg-transparent px-2 py-3 text-white placeholder:text-white/30 focus:outline-none font-mono-space"
          />
          <button className="btn-primary" type="submit" data-testid="verify-btn" disabled={busy}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Verify
          </button>
        </form>

        {notFound && (
          <div className="mt-8 glass rounded-2xl p-6 text-white/70 flex items-center gap-3 justify-center" data-testid="verify-notfound">
            <XCircle className="w-5 h-5 text-red-400" /> No registration found for that number.
          </div>
        )}

        {result && (
          <div className="mt-10 glass rounded-3xl p-8 text-left" data-testid="verify-result">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-gold" />
              <div className="overline text-gold">Verified Registration</div>
            </div>
            <div className="font-mono-space text-2xl text-gold mt-2">{result.registration_number}</div>

            <div className="mt-8 grid sm:grid-cols-2 gap-6 border-t border-white/10 pt-6">
              <Item k="Registered To" v={result.customer_name} />
              <Item k="Planet" v={result.planet === "moon" ? "The Moon" : "Mars"} />
              <Item k="Plot Location" v={result.marker_label} />
              <Item k="Coordinates" v={`LAT ${result.lat.toFixed(2)}° · LON ${result.lon.toFixed(2)}°`} mono />
              <Item k="Plot Size" v={`${result.acres} ${result.acres === 1 ? "Acre" : "Acres"}`} />
              <Item k="Purchase Date" v={new Date(result.purchase_date).toLocaleDateString()} />
              <Item k="Registration Status" v={<span className="text-emerald-400">Valid</span>} />
              <Item k="Courier Status" v={<span className="text-gold">{result.courier_status?.toUpperCase()}</span>} />
              {result.courier_company && (
                <Item k="Courier Partner" v={result.courier_company} />
              )}
              {result.tracking_number && (
                <Item k="Tracking Number" v={<span className="font-mono-space">{result.tracking_number}</span>} mono />
              )}
            </div>

            <div className="mt-8 text-xs text-white/50 leading-relaxed">
              This verification page confirms the existence and metadata of a Teuralink commemorative registration.
              It does not provide a downloadable deed or represent legal ownership.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Item({ k, v, mono }) {
  return (
    <div>
      <div className="overline">{k}</div>
      <div className={`mt-1 ${mono ? "font-mono-space text-sm" : "text-base"} text-white`}>{v}</div>
    </div>
  );
}
